import axios from 'axios';

// Base URL for API calls - use environment variable or fallback to localhost for development
const API_URL = process.env.NODE_ENV === 'production'
  ? (process.env.REACT_APP_API_BASE_URL || 'https://apidev.birdie.house')
  : 'http://localhost:3001'; // Use your local backend port

console.log('API URL for payments:', API_URL);

/**
 * Process payment for membership
 * @param {string} userId - User ID
 * @param {string} membershipType - Type of membership (basic, unlimited)
 * @param {number} amount - Payment amount
 * @param {string} paymentMethodId - Stripe payment method ID
 * @returns {Promise} - Promise with payment result
 */
export const processPayment = async (userId, membershipType, amount, paymentMethodId) => {
  try {
    // First create a payment intent
    const intentResponse = await axios.post(`${API_URL}/api/create-payment-intent`, {
      amount,
      currency: 'usd',
      metadata: {
        userId,
        membershipType,
        paymentType: 'membership'
      }
    }, {
      withCredentials: true // Important for sessions/cookies
    });
    
    const { clientSecret, paymentIntentId } = intentResponse.data;
    
    // Then confirm the payment
    const confirmResponse = await axios.post(`${API_URL}/api/confirm-payment`, {
      paymentIntentId,
      membershipDetails: {
        userId,
        membershipType,
        amount,
        paymentMethodId
      }
    }, {
      withCredentials: true // Important for sessions/cookies
    });
    
    return confirmResponse.data;
  } catch (error) {
    console.error('Payment processing error:', error);
    if (error.response) {
      throw new Error(error.response.data.message || 'Payment processing failed');
    } else if (error.request) {
      throw new Error('No response from server. Please try again later.');
    } else {
      throw new Error('Error setting up payment request');
    }
  }
};

/**
 * Process payment for bookings
 * @param {string} userId - User ID
 * @param {Array} bookingIds - Array of booking IDs
 * @param {number} amount - Payment amount
 * @param {string} paymentMethodId - Stripe payment method ID
 * @returns {Promise} - Promise with payment result
 */
export const processBookingPayment = async (userId, bookingIds, amount, paymentMethodId) => {
  try {
    // First create a payment intent
    const intentResponse = await axios.post(`${API_URL}/api/create-payment-intent`, {
      amount,
      currency: 'usd',
      metadata: {
        userId,
        bookingIds: JSON.stringify(bookingIds), // Convert array to string for metadata
        paymentType: 'booking'
      }
    }, {
      withCredentials: true // Important for sessions/cookies
    });
    
    const { clientSecret, paymentIntentId } = intentResponse.data;
    
    // Then confirm the payment
    const confirmResponse = await axios.post(`${API_URL}/api/confirm-payment`, {
      paymentIntentId,
      bookingDetails: {
        userId,
        bookingIds,
        amount,
        paymentMethodId
      }
    }, {
      withCredentials: true // Important for sessions/cookies
    });
    
    return confirmResponse.data;
  } catch (error) {
    console.error('Booking payment processing error:', error);
    if (error.response) {
      throw new Error(error.response.data.message || 'Booking payment processing failed');
    } else if (error.request) {
      throw new Error('No response from server. Please try again later.');
    } else {
      throw new Error('Error setting up booking payment request');
    }
  }
};

/**
 * Get payment intent client secret for Stripe Elements (Membership)
 * @param {string} userId - User ID
 * @param {string} membershipType - Type of membership
 * @param {number} amount - Payment amount
 * @returns {Promise} - Promise with client secret
 */
export const createPaymentIntent = async (userId, membershipType, amount) => {
  try {
    console.log('Creating payment intent with:', { userId, membershipType, amount });
    
    // Validate inputs
    if (!userId) console.warn('Warning: userId is missing');
    if (!membershipType) console.warn('Warning: membershipType is missing');
    if (!amount || isNaN(amount) || amount <= 0) {
      console.error('Invalid amount:', amount);
      throw new Error('Invalid amount provided');
    }
    
    const response = await axios.post(`${API_URL}/api/create-payment-intent`, {
      amount,
      currency: 'usd',
      metadata: {
        userId,
        membershipType,
        paymentType: 'membership'
      }
    }, {
      withCredentials: true // Important for sessions/cookies
    });
    
    console.log('Payment intent created successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Create payment intent error:', error);
    
    // Log more details about the request
    console.error('Request was sent to:', `${API_URL}/api/create-payment-intent`);
    
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
      throw new Error(error.response.data.message || 'Failed to create payment intent');
    } else if (error.request) {
      console.error('No response received from server');
      throw new Error('No response from server. Please try again later.');
    } else {
      console.error('Error setting up request:', error.message);
      throw new Error('Error setting up payment request');
    }
  }
};

/**
 * Get payment intent client secret for Stripe Elements (Booking)
 * @param {string} userId - User ID
 * @param {Array} bookingIds - Array of booking IDs
 * @param {number} amount - Payment amount
 * @returns {Promise} - Promise with client secret
 */
export const createBookingPaymentIntent = async (userId, bookingIds, amount) => {
  try {
    console.log('Creating booking payment intent with:', { userId, bookingIds, amount });
    
    // Validate inputs
    if (!userId) console.warn('Warning: userId is missing');
    if (!bookingIds || !Array.isArray(bookingIds) || bookingIds.length === 0) {
      console.warn('Warning: bookingIds is missing or invalid');
    }
    if (!amount || isNaN(amount) || amount <= 0) {
      console.error('Invalid amount:', amount);
      throw new Error('Invalid amount provided');
    }
    
    const response = await axios.post(`${API_URL}/api/create-payment-intent`, {
      amount,
      currency: 'usd',
      metadata: {
        userId,
        bookingIds: JSON.stringify(bookingIds), // Convert array to string for metadata
        paymentType: 'booking'
      }
    }, {
      withCredentials: true // Important for sessions/cookies
    });
    
    console.log('Booking payment intent created successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Create booking payment intent error:', error);
    
    // Log more details about the request
    console.error('Request was sent to:', `${API_URL}/api/create-payment-intent`);
    
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
      throw new Error(error.response.data.message || 'Failed to create booking payment intent');
    } else if (error.request) {
      console.error('No response received from server');
      throw new Error('No response from server. Please try again later.');
    } else {
      console.error('Error setting up request:', error.message);
      throw new Error('Error setting up booking payment request');
    }
  }
};

/**
 * Verify payment status
 * @param {string} paymentIntentId - Stripe payment intent ID
 * @returns {Promise} - Promise with payment status
 */
export const verifyPayment = async (paymentIntentId) => {
  try {
    // Use the confirm-payment endpoint instead of a dedicated verify endpoint
    const response = await axios.post(`${API_URL}/api/confirm-payment`, {
      paymentIntentId,
      membershipDetails: {} // Empty membership details for verification only
    }, {
      withCredentials: true // Important for sessions/cookies
    });
    return response.data;
  } catch (error) {
    console.error('Verify payment error:', error);
    if (error.response) {
      throw new Error(error.response.data.message || 'Failed to verify payment');
    } else if (error.request) {
      throw new Error('No response from server. Please try again later.');
    } else {
      throw new Error('Error verifying payment');
    }
  }
}; 